
import RcDomainObject from "@/domain/common/RcDomainObject.js";

import AnswerMap from "@/domain/model/answer/AnswerMap.js";
import Vehicle from "@/domain/model/vehicle/Vehicle.js";
import Schedule from "@/domain/model/schedule/Schedule.js";
import StringUtils from '@/utils/StringUtils.js';

import RcDate from '@/domain/session/time/RcDate.js';

export default class Result extends RcDomainObject {

  static MODEL_NAME = "VehicleInspectionResult";

  static Fields = {
    RESULT_TYPE: "hasResultType",
    DEFECT_UNFIXED_TYPE: "unfixedType",
    ODOMETER: "odometer",
    VEHICLE: Vehicle.MODEL_NAME,
    SCHEDULE: "VehicleSchedule",
    ANSWERS: "VehicleInspectionAnswerMap",
    DEFECT_TYPE: "defectType",
    ATTACHED_DATE: "attachedDate",
    DETACHED_DATE: "detachedDate",
    Attached: "attached",
    LOCATION: "location",
    AGREEMENT: "agreement",
    COMPANY: "Company",
  }

  static Values = {
    DefectType: {
      DEFECT_TYPE_NONE: "None",
      DEFECT_TYPE_MINOR: "Minor",
      DEFECT_TYPE_MAJOR: "Major",
      DEFECT_TYPE_OOS: "Oos",
      DEFECT_TYPE_NA: "NA",
    },
    VehicleType: {
      RESULT_TYPE_VEHICLE: "vehicle",
    },
    ResultType: {
      RESULT_TYPE_VEHICLE: "vehicle",
      RESULT_TYPE_INITIAL: "Initial",
      RESULT_TYPE_ENROUTE: "Enroute",
      RESULT_TYPE_VEHICLE_INITIAL: "vehicleInitial",
      RESULT_TYPE_VEHICLE_ENROUTE: "vehicleEnroute",
      RESULT_TYPE_TRAILER_INITIAL: "trailerInitial",
      RESULT_TYPE_TRAILER_ENROUTE: "trailerEnroute",
    },
    AttachedType: {
      RESULT_ATTACHED: "attached",
      RESULT_ATTACHED_TRUE: "true",
      RESULT_ATTACHED_FALSE: "false",
    },
  }

  static mostSevereDefectType(defectOne, defectTwo) {
    if (StringUtils.eic(Result.Values.DefectType.DEFECT_TYPE_MAJOR, defectOne) || StringUtils.eic(Result.Values.DefectType.DEFECT_TYPE_MAJOR, defectTwo)) {
      return Result.Values.DefectType.DEFECT_TYPE_MAJOR;
    }

    if (StringUtils.eic(Result.Values.DefectType.DEFECT_TYPE_MINOR, defectOne) || StringUtils.eic(Result.Values.DefectType.DEFECT_TYPE_MINOR, defectTwo)) {
      return Result.Values.DefectType.DEFECT_TYPE_MINOR;
    }
    return Result.Values.DefectType.DEFECT_TYPE_NONE;
  }

  constructor(domain, data) {
    super(domain, data, Result.MODEL_NAME);
  }

  fields() {
    return Result.Fields;
  }

  vehicle() {
    var obj = this.get(Result.Fields.VEHICLE);
    return new Vehicle(this.domain, obj);
  }

  withVehicle(vehicle) {
    this.put(Result.Fields.VEHICLE, vehicle.data || vehicle);
    return this;
  }

  company() {
    return this.get(Result.Fields.COMPANY);
  }

  withCompany(company) {
    this.put(Result.Fields.COMPANY, company.data || company);
    return this;
  }

  agreement() {
    return this.getBoolean(Result.Fields.AGREEMENT);
  }

  withAgreement(agreed) {
    this.put(Result.Fields.AGREEMENT, agreed);
    return this;
  }

  odometer() {
    return this.get(Result.Fields.ODOMETER);
  }

  withOdometer(odometer) {
    var cleanOdometer = StringUtils.toString(odometer).replace(/,/g, "");
    this.put(Result.Fields.ODOMETER, cleanOdometer);
    return this;
  }

  location() {
    return this.getString(Result.Fields.LOCATION);
  }

  withLocation(location) {
    this.put(Result.Fields.LOCATION, location);
    return this;
  }

  defectType() {
    return this.getString(Result.Fields.DEFECT_TYPE);
  }

  withDefectType(value) {
    this.put(Result.Fields.DEFECT_TYPE, value);
    return this;
  }

  withDefectTypeNone() {
    return this.withDefectType(Result.Values.DefectType.DEFECT_TYPE_NONE);
  }

  withDefectTypeMinor() {
    return this.withDefectType(Result.Values.DefectType.DEFECT_TYPE_MINOR);
  }

  withDefectTypeMajor() {
    return this.withDefectType(Result.Values.DefectType.DEFECT_TYPE_MAJOR);
  }

  withDefectTypeClear() {
    return this.withDefectType("");
  }

  isDefectTypeClear() {
    return StringUtils.isEmpty(this.defectType());
  }

  isDefectTypeNone() {
    return this.defectType() === Result.Values.DefectType.DEFECT_TYPE_NONE;
  }

  isDefectTypeGood() {
    var defectType = this.defectType();
    if (StringUtils.eic(defectType, "major")) {
      return false;
    }
    if (StringUtils.eic(defectType, "minor")) {
      return false;
    }
    return true;
  }

  isDefectTypeMinor() {
    var defectType = this.defectType();
    if (StringUtils.eic(defectType, "minor")) {
      return true;
    }
    return false;
  }

  isDefectTypeMajor() {
    var defectType = this.defectType();
    if (StringUtils.eic(defectType, "major")) {
      return true;
    }
    return false;
  }

  resultType() {
    return this.getString(Result.Fields.RESULT_TYPE);
  }

  hasResultType() {
    return !StringUtils.isEmpty(this.resultType());
  }

  withResultType(resultType) {
    this.put(Result.Fields.RESULT_TYPE, resultType);
    return this;
  }

  withResultTypeVehicleInitial() {
    return this.withResultType(Result.Values.ResultType.RESULT_TYPE_VEHICLE_INITIAL);
  }

  withResultTypeVehicleEnroute() {
    return this.withResultType(Result.Values.ResultType.RESULT_TYPE_VEHICLE_ENROUTE);
  }

  withResultTypeTrailerInitial() {
    return this.withResultType(Result.Values.ResultType.RESULT_TYPE_TRAILER_INITIAL);
  }

  withResultTypeTrailerEnroute() {
    return this.withResultType(Result.Values.ResultType.RESULT_TYPE_TRAILER_ENROUTE);
  }

  isInitial() {
    var resultType = this.getString(Result.Fields.RESULT_TYPE);
    return resultType.indexOf("Initial") !== -1;
  }

  isEnroute() {
    var resultType = this.getString(Result.Fields.RESULT_TYPE);
    return resultType.indexOf("Enroute") !== -1;
  }

  hasSchedule() {
    return this.schedule().isNotNew();
  }

  schedule() {
    var scheduleData = this.get(Result.Fields.SCHEDULE);
    return new Schedule(this.domain, scheduleData);
  }

  withSchedule(schedule) {
    this.put(Result.Fields.SCHEDULE, schedule.data || schedule);
    return this;
  }

  answers() {
    var data = this.get(Result.Fields.ANSWERS);
    return new AnswerMap(this.domain, data);
  }

  withAnswers(answers) {
    this.put(Result.Fields.ANSWERS, answers.data || answers);
    return this;
  }

  withAnswer(answer) {
    var answers = this.answers();
    var found = answers.find(answer.question());
    if (found.isNew()) {
      answers.add(answer);
    } else {
      var options = answer.options();
      var keys = options.keys();
      for (var i = 0; i < keys.length; i++) {
        var option = options.getOption(keys[i]);
        found.withOption(option);
      }
    }
    return this.withAnswers(answers);
  }

  remove(answer) {
    var answers = this.answers();
    answers.remove(answer);
    return this.withAnswers(answers);
  }

  withUnfixedDefectTypeMajor() {
    this.put(Result.Fields.DEFECT_UNFIXED_TYPE,
      Result.Values.DefectType.DEFECT_TYPE_MAJOR);
    return this;
  }

  withUnfixedDefectTypeMinor() {
    this.put(Result.Fields.DEFECT_UNFIXED_TYPE,
      Result.Values.DefectType.DEFECT_TYPE_MINOR);
    return this;
  }

  withUnfixedDefectTypeNone() {
    this.put(Result.Fields.DEFECT_UNFIXED_TYPE,
      Result.Values.DefectType.DEFECT_TYPE_NONE);
    return this;
  }

  mostSevereDefectTypeCalculation() {
    var answers = this.answers();
    if (answers.hasUnfixedMajors()) {
      this.withDefectTypeMajor();
    } else if (answers.hasUnfixedMinors()) {
      this.withDefectTypeMinor();
    } else {
      this.withDefectTypeNone();
    }
    return this;
  }

  mostSevereUnfixedDefectTypeCalculation() {
    var answers = this.answers();
    if (answers.hasUnfixedMajors()) {
      this.withUnfixedDefectTypeMajor();
    } else if (answers.hasUnfixedMinors()) {
      this.withUnfixedDefectTypeMinor();
    } else {
      this.withUnfixedDefectTypeNone();
    }
    return this;
  }

  attachedDate() {
    return this.getInt(Result.Fields.ATTACHED_DATE);
  }

  withAttachedDate(value) {
    this.put(Result.Fields.ATTACHED_DATE, value);
    return this;
  }

  withAttachedDateNow() {
    return this.withAttachedDate(Date.now());
  }

  attachedDateV2() {
    var date = this.attachedDate();
    var timeZone = this.timeZone();
    var rcDate = new RcDate(new Date(date), timeZone);
    return rcDate;
  }

  attachedDateDisplay() {
    var date = this.attachedDateV2();
    return date.displayAs_YYYY_MM_DD_HH_MM_SS_A();
  }

  detachedDate() {
    return this.getInt(Result.Fields.DETACHED_DATE);
  }

  withDetachedDate(value) {
    this.put(Result.Fields.DETACHED_DATE, value);
    return this;
  }

  withDetachedDateNow() {
    return this.withDetachedDate(Date.now());
  }

  withDetachedDateCleared() {
    return this.withDetachedDate(0);
  }

  detachedDateV2() {
    var date = this.detachedDate();
    var timeZone = this.timeZone();
    var rcDate = new RcDate(new Date(date), timeZone);
    return rcDate;
  }

  detachedDateDisplay() {
    var date = this.detachedDateV2();
    return date.displayAs_YYYY_MM_DD_HH_MM_SS_A();
  }

  isVehicle() {
    var rt = this.resultType();
    var vehiclePrefix = (Result.Values.ResultType && Result.Values.ResultType.RESULT_TYPE_VEHICLE) || (Result.Values.VehicleType && Result.Values.VehicleType.RESULT_TYPE_VEHICLE) || "vehicle";
    return typeof rt === 'string' && rt.startsWith(vehiclePrefix);
  }

  isAttached() {
    if (this.isVehicle()) {
      return true;
    }
    return this.getBoolean(Result.Fields.Attached);
  }

  withAttached(inservice = true) {
    this.put(Result.Fields.Attached, inservice ? "true" : "false");
    return this;
  }

  withDetached() {
    return this.withAttached(false);
  }

  isDetached() {
    return !this.isAttached();
  }

  markAsAttached() {
    return this.withAttachedDateNow().withAttached().withDetachedDateCleared();
  }

  markAsDetached() {
    return this.withDetachedDateNow().withDetached();
  }

  mostSevereUnfixedDefectType() {
    return this.getString(Result.Fields.DEFECT_UNFIXED_TYPE);
  }

  defectTypeDisplay() {
    return Result.defectDisplayValue(this.mostSevereUnfixedDefectType());
  }

  static defectDisplayValue(value) {
    var display = "No Defects";
    if (StringUtils.cic(value, "major")) {
      display = "Major Defect";
    }
    if (StringUtils.cic(value, "minor")) {
      display = "Minor Defect";
    }
    return display;
  }
}